import { useMutation, useQuery } from "@apollo/client";
import * as Dialog from "@radix-ui/react-dialog";
import { useContext, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Button from "src/components/Button";
import DialogModal from "src/components/Dialog";
import Icon from "src/components/Icon";
import { AppContext, ValueProps } from "src/context";
import { ATTACH_EVENT_WALLET_TO_SUBACCOUNT } from "src/graphql/mutations";
import { GetEvent, GetProfileDetails } from "src/graphql/queries";
import AttireIcon from "src/layouts/dashboard/Icons/Attire";
import useAppStore from "src/store/utils";
import { user } from "src/types/api";
import {
  AttachEventWalletToSubaccount,
  AttachEventWalletToSubaccountVariables,
} from "src/utils/gql_types";
import AddNewAttire from "../modals/AddNewAttire";
import styles from "./styles.module.scss";

interface Props {
  groupId: string | undefined;
  groupName: string | null;
}

function EmptyState({ groupId, groupName }: Props) {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);

  const isPaymentSetup = useContext(AppContext)?.isPaymentSetup;

  const { event } = useAppStore((state) => state);

  const setIsPaymentSetup = useContext(AppContext)?.setIsPaymentSetup;

  const { data: profile } = useQuery<user>(GetProfileDetails);

  const [isAttachAccountOpen, setIsAttachAccountOpen] = useState(false);

  const [isInviteCoupleOpen, setIsInviteCoupleOpen] = useState(false);

  const contextValue = useContext<ValueProps | null>(AppContext);

  const isAdmin = contextValue?.isAdmin;

  const setAddTeam = useContext(AppContext)?.setAddTeam;

  const handleAddAttireClick = () => {
    const kycStatus = profile?.user?.kyc_record?.status;

    if (!isPaymentSetup) {
      if (!isAdmin) {
        setIsInviteCoupleOpen(true);
        return;
      }
      if (kycStatus === "completed") {
        setIsAttachAccountOpen(true);
      } else {
        navigate("/payment-setup", {
          state: {
            from: "aso-ebi",
            subFrom: `/aso-ebi/${groupId}?groupName=${groupName}`,
          },
        });
      }
      return;
    }

    setOpen(true);
  };

  const [attachEventWalletToSubaccount, { loading: attachWalletLoading }] =
    useMutation<
      AttachEventWalletToSubaccount,
      AttachEventWalletToSubaccountVariables
    >(ATTACH_EVENT_WALLET_TO_SUBACCOUNT);

  const handleAddAccount = () => {
    attachEventWalletToSubaccount({
      variables: {
        event_id: event.uuid,
        currency: "ngn",
      },
      onCompleted() {
        toast.success("Bank details successfully attached");
        setIsPaymentSetup && setIsPaymentSetup(true);
        setIsAttachAccountOpen(false);
      },
      onError(error) {
        if (error?.message !== "unauthenticated") {
          toast.error(
            <p className="toast">
              {error?.message ??
                "An error occured uploading your bank details. Please try again"}
            </p>
          );
        } else {
          navigate("/clear");
        }
      },
      refetchQueries: [{ query: GetEvent, variables: { eventId: event?.id } }],
    });
  };

  return (
    <>
      <DialogModal
        open={isAttachAccountOpen}
        onOpenChange={setIsAttachAccountOpen}
        asChild
      >
        <div className="rounded bg-white p-8">
          <h1 className="mb-2 text-2xl font-bold">Add your Naira account</h1>
          <p className="mb-4 text-gray-500">Add your saved Naira acount</p>

          <div className="mb-12 flex flex-col gap-8">
            <div className="flex w-full flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
              <div className="flex items-center gap-4 rounded border p-2">
                {/* <div className="h-14 w-14 flex-shrink-0 rounded-full bg-orange-500"></div> */}
                <p className="uppercase text-gray-500">
                  {profile?.user?.kyc_record?.bank_details[0]?.bank_name}
                </p>
              </div>

              {/* <div className="flex gap-4"> */}
              <div className="rounded border p-2">
                <p className="uppercase text-gray-500">
                  {profile?.user?.kyc_record?.bank_details[0]?.account_name}
                </p>
              </div>

              <div className="w-fit rounded border p-2">
                <p className="uppercase text-gray-500">
                  {profile?.user?.kyc_record?.bank_details[0]?.account_number}
                </p>
              </div>
            </div>
            {/* </div> */}

            <div className="flex h-[100px] w-full items-center justify-center rounded border border-[#B95900] p-4">
              <p className="text-sm text-gray-500 sm:text-base">
                This is where your money would be sent to automatically by our
                payment partners
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <div className="flex w-full gap-2 sm:w-3/5 sm:gap-4">
              <Button
                design="secondary"
                label="Cancel"
                className="text-sm sm:text-base"
                onClick={() => setIsAttachAccountOpen(false)}
              >
                Cancel
              </Button>
              <Button
                loader={attachWalletLoading}
                label="Add Account"
                className="text-sm sm:text-base"
                onClick={handleAddAccount}
              >
                Add Account
              </Button>
            </div>
          </div>
        </div>
      </DialogModal>

      <DialogModal open={open} onOpenChange={setOpen} asChild>
        <AddNewAttire groupId={groupId} setOpen={setOpen} />
      </DialogModal>

      <Dialog.Root
        open={isInviteCoupleOpen}
        onOpenChange={setIsInviteCoupleOpen}
      >
        {/* <Dialog.Trigger>

        </Dialog.Trigger> */}

        <Dialog.Portal>
          <Dialog.Overlay className="fixed left-0 top-0 z-[] flex h-full w-full items-center justify-center gap-3 overflow-y-auto bg-[#00000080] px-4">
            <Dialog.Content>
              <div className="max-w-[500px] rounded bg-white p-4 md:p-8">
                <div className="mb-4 flex items-center gap-2">
                  <Icon iconName="disclaimer" />
                  <h2 className="text-xl font-bold">Naira setup required</h2>
                </div>

                <p className="mb-4 text-sm md:text-base">
                  Only the couple can set up naira to receive payments for
                  asoebi. Please invite the bride or groom to complete this
                  setup so you can continue.
                </p>

                <div className="flex gap-2 sm:gap-3">
                  <Button
                    label="Cancel"
                    design="secondary"
                    className="text-sm sm:text-base"
                    onClick={() => setIsInviteCoupleOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    label="Invite Couple"
                    className="text-sm sm:text-base"
                    onClick={() => {
                      setIsInviteCoupleOpen(false);
                      setTimeout(() => {
                        setAddTeam && setAddTeam(true);
                      }, 10);
                    }}
                  >
                    Invite couple
                  </Button>
                </div>
              </div>
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>

      <div className={styles["EmptyState"]}>
        <div className={styles["EmptyState--main"]}>
          <div className="mb-[30px]">
            <AttireIcon />
          </div>
          <p>Add clothes specific to this group.</p>
          <Button
            onClick={handleAddAttireClick}
            label="add-attire"
            className="!text-white"
          >
            Add attire
          </Button>
        </div>
      </div>
    </>
  );
}

export default EmptyState;
